import { Link } from '@tanstack/react-router';
import { CalendarClock, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useNotifications } from '../lib/notifications';
import { useInReportEditor } from '../lib/use-in-editor';

const storageKey = (key: string, day: string) => `qr.plan.hidden:${key}:${day}`;

/**
 * A gentle note about the lab's plan: it ends soon, it has ended, or the wallet is low. It never
 * blocks anything. "Hide" quiets it for the rest of the day; it comes back tomorrow.
 */
export function PlanBanner() {
  const n = useNotifications();
  const editing = useInReportEditor();
  const notice = n.planNotice;
  const [, bump] = useState(0);
  // An empty wallet is shown by the red banner for urgent messages.
  if (!notice || notice.tone === 'alert') return null;
  let hidden = false;
  try {
    hidden = localStorage.getItem(storageKey(notice.key, n.today)) === '1';
  } catch {
    // storage blocked: the note simply stays
  }
  if (hidden) return null;
  const Icon = n.planStatus.kind === 'per_report' ? Wallet : CalendarClock;
  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-[13px] text-ink sm:px-6 ${
        editing ? 'max-md:hidden ' : ''
      }${notice.tone === 'warn' ? 'border-warn/25 bg-amber-50' : 'border-brand/20 bg-brand-soft'}`}
    >
      <Icon size={15} className={notice.tone === 'warn' ? 'text-warn' : 'text-brand'} />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{notice.title}.</span>{' '}
        <span className="text-slate">{notice.body}</span>
      </span>
      <Link
        to="/about"
        hash="plans"
        className="font-semibold whitespace-nowrap text-brand underline"
      >
        See plans
      </Link>
      <button
        type="button"
        className="whitespace-nowrap text-slate hover:text-ink"
        aria-label="Hide this note for today"
        onClick={() => {
          try {
            localStorage.setItem(storageKey(notice.key, n.today), '1');
          } catch {
            // ignore
          }
          bump((x) => x + 1);
        }}
      >
        Hide
      </button>
    </div>
  );
}
